import React from "react";
import { motion } from "framer-motion";
import {
  Briefcase,
  Landmark,
  Globe2,
  LineChart,
  Sprout,
  ArrowUpRight,
} from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";

const ICONS = {
  "private-equity": Briefcase,
  "debt-finance": Landmark,
  "trade-finance": Globe2,
  advisory: LineChart,
  "impact-investing": Sprout,
};

export const Services = () => {
  const { t } = useLanguage();

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-[#F8F9FA] py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        {/* Header */}
        <div className="max-w-3xl mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="flex items-center gap-4 mb-8"
          >
            <span className="h-px w-10 bg-[#0D2F4C]" />
            <span
              data-testid="services-overline"
              className="text-xs uppercase tracking-[0.3em] font-bold text-[#0D2F4C]"
            >
              {t.services.overline}
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            data-testid="services-title"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.1] text-[#0D2F4C] tracking-tight mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t.services.title}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-[#4B5563] text-lg leading-relaxed"
          >
            {t.services.subtitle}
          </motion.p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#0D2F4C]/10 border border-[#0D2F4C]/10">
          {t.services.items.map((item, i) => {
            const Icon = ICONS[item.key] || Briefcase;
            return (
              <motion.div
                key={item.key}
                data-testid={`service-card-${item.key}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                className="group bg-white p-10 lg:p-12 relative overflow-hidden cursor-pointer transition-colors duration-500 hover:bg-[#0D2F4C]"
              >
                <div className="flex items-start justify-between mb-12">
                  <Icon
                    className="text-[#0D2F4C] group-hover:text-white transition-colors duration-500"
                    size={32}
                    strokeWidth={1.2}
                  />
                  <ArrowUpRight
                    className="text-[#0D2F4C]/40 group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-500"
                    size={20}
                    strokeWidth={1.5}
                  />
                </div>

                <div className="text-xs uppercase tracking-[0.25em] text-[#0D2F4C]/50 group-hover:text-white/60 transition-colors duration-500 mb-3">
                  0{i + 1}
                </div>

                <h3
                  className="font-serif text-2xl sm:text-3xl text-[#0D2F4C] group-hover:text-white transition-colors duration-500 mb-5 leading-tight"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {item.title}
                </h3>

                <p className="text-sm leading-relaxed text-[#4B5563] group-hover:text-white/80 transition-colors duration-500">
                  {item.description}
                </p>
              </motion.div>
            );
          })}

          {/* Filler decorative cell */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="hidden lg:flex bg-[#0D2F4C] p-10 lg:p-12 flex-col justify-between"
          >
            <div className="text-xs uppercase tracking-[0.3em] text-white/60">
              Richild Invest GBC
            </div>
            <h3
              className="font-serif text-3xl text-white italic font-light leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t.footer.tagline}
            </h3>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
